/**
 * CompanySelector.jsx - Dropdown for picking the active BC company.
 *
 * Placed in the Topbar. Lists "All branches" plus the companies from
 * CompanyContext. Visually mirrors the design tokens used elsewhere.
 */

import { useState, useRef, useEffect } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useCompany } from "../company/CompanyContext.jsx";

export default function CompanySelector() {
  const { companies, selectedCompany, setSelectedCompany, allBranchesOption, loading, liveFromBc } =
    useCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const items = [allBranchesOption, ...companies];
  const current = selectedCompany || allBranchesOption;

  return (
    <div ref={ref} style={s.wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={s.button}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={
          liveFromBc
            ? "Companies loaded from Business Central"
            : "Showing fallback list - BC client secret not set yet"
        }
      >
        <Building2 size={14} style={{ color: "var(--ink-3)" }} />
        <span style={s.label}>
          <span style={s.labelTitle}>Branch</span>
          <span style={s.labelValue}>{current.displayName || current.name}</span>
        </span>
        <ChevronDown size={14} style={{ color: "var(--ink-3)" }} />
        {loading ? <span style={s.dot} aria-label="loading" /> : null}
      </button>

      {open && (
        <div role="listbox" style={s.menu}>
          {items.map((c) => {
            const isSelected = (selectedCompany?.id || "all") === (c.id || "all");
            return (
              <button
                key={c.id || "all"}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  // "All branches" shows all but uses first company for API (BC requires scope)
                  if (c.id === "all") {
                    setSelectedCompany({ id: "all", name: "All branches", displayName: "All branches" });
                  } else {
                    setSelectedCompany(c);
                  }
                  setOpen(false);
                }}
                style={{
                  ...s.item,
                  ...(isSelected ? s.itemActive : {}),
                }}
              >
                <span style={s.itemLabel}>
                  {c.id === "all" ? (
                    <span style={{ fontWeight: 600 }}>{c.displayName}</span>
                  ) : (
                    <>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      {c.displayName && c.displayName !== c.name ? (
                        <span style={s.itemSub}>{c.displayName}</span>
                      ) : null}
                    </>
                  )}
                </span>
                {isSelected && <Check size={14} style={{ color: "var(--accent)" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { position: "relative", display: "inline-block" },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 10px",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    color: "var(--ink)",
    cursor: "pointer",
    minWidth: 220,
    transition: "background 0.15s, border-color 0.15s",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 1,
    lineHeight: 1.2,
    flex: 1,
    textAlign: "left",
  },
  labelTitle: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--ink-3)",
  },
  labelValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--ink)",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    minWidth: 280,
    maxHeight: 320,
    overflowY: "auto",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    boxShadow: "var(--shadow-lg)",
    padding: 6,
    zIndex: 50,
  },
  item: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    border: "none",
    background: "transparent",
    borderRadius: "var(--radius-sm)",
    color: "var(--ink)",
    cursor: "pointer",
    textAlign: "left",
  },
  itemActive: {
    background: "var(--line-2)",
  },
  itemLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  itemSub: {
    fontSize: 11,
    color: "var(--ink-3)",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "var(--accent)",
    marginLeft: 6,
  },
};